import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route } from "react-router-dom";
import { PhoneBarProvider } from "@/contexts/PhoneBarContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { StickyPhoneBar } from "@/components/StickyPhoneBar";
import { AdminLayout } from "@/components/admin/AdminLayout";
import Index from "./pages/Index";
import CityLanding from "./pages/CityLanding";
import ServiceLanding from "./pages/ServiceLanding";
import WelcomeLanding from "./pages/WelcomeLanding";
import AdminLogin from "./pages/AdminLogin";
import Dashboard from "./pages/admin/Dashboard";
import AdminLeads from "./pages/AdminLeads";
import PhoneLeads from "./pages/admin/PhoneLeads";
import PhoneNumbers from "./pages/admin/PhoneNumbers";
import SMSConversations from "./pages/admin/SMSConversations";
import SMSPhoneNumbers from "./pages/admin/SMSPhoneNumbers";
import SMSPhoneConversations from "./pages/admin/SMSPhoneConversations";
import SMSConversation from "./pages/admin/SMSConversation";
import SMSSettings from "./pages/admin/SMSSettings";
import AllLeads from "./pages/admin/AllLeads";
import AdminProjects from "./pages/AdminProjects";
import AdminPageProjects from "./pages/AdminPageProjects";
import AdminServices from "./pages/AdminServices";
import AdminAnalytics from "./pages/AdminAnalytics";
import CityGroups from "./pages/admin/CityGroups";
import FeaturedContent from "./pages/admin/FeaturedContent";
import AdminSettings from "./pages/AdminSettings";
import AdminCategories from "./pages/AdminCategories";
import Appointments from "./pages/admin/Appointments";
import BookAppointment from "./pages/BookAppointment";
import PriceBook from "./pages/admin/PriceBook";
import Estimates from "./pages/admin/Estimates";
import EstimateBuilder from "./pages/admin/EstimateBuilder";
import SalesPresentationMode from "./pages/admin/SalesPresentationMode";
import Customers from "./pages/admin/Customers";
import NewCustomer from "./pages/admin/NewCustomer";
import EditCustomer from "./pages/admin/EditCustomer";
import CustomerDetail from "./pages/admin/CustomerDetail";
import ShingleColors from "./pages/admin/ShingleColors";
import InstallationPhases from "./pages/admin/InstallationPhases";
import PhaseProducts from "./pages/admin/PhaseProducts";
import ProductCategories from "./pages/admin/ProductCategories";
import ProposalView from "./pages/admin/ProposalView";
import ProposalGallery from "./pages/admin/ProposalGallery";
import PublicProposalView from "./pages/PublicProposalView";
import ChangeRequests from "./pages/admin/ChangeRequests";
import TierConfiguration from "./pages/admin/TierConfiguration";

import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <PhoneBarProvider>
          <Toaster />
          <Sonner />
          <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/book-appointment" element={<BookAppointment />} />
          <Route path="/:serviceSlug/:cityName" element={<ServiceLanding />} />
          <Route path="/:serviceSlug" element={<ServiceLanding />} />
          <Route path="/city/:cityName?" element={<CityLanding />} />
          <Route path="/welcome" element={<WelcomeLanding />} />
          <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Full-screen routes (no admin layout) */}
            <Route path="/admin/sms-phone-numbers/:phoneNumberId/conversations/:conversationId" element={<SMSConversation />} />
            <Route path="/admin/estimates/presentation/:estimateId" element={<SalesPresentationMode />} />
            <Route path="/admin/estimates/:estimateId/proposal" element={<ProposalView />} />
            <Route path="/proposal/public/:estimateId/:accessToken" element={<PublicProposalView />} />
            
            {/* Admin routes with sidebar layout */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="all-leads" element={<AllLeads />} />
              <Route path="calls" element={<PhoneLeads />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="phone-leads" element={<PhoneLeads />} />
              <Route path="phone-numbers" element={<PhoneNumbers />} />
              <Route path="sms-conversations" element={<SMSConversations />} />
              <Route path="sms-phone-numbers" element={<SMSPhoneNumbers />} />
              <Route path="sms-phone-numbers/:phoneNumberId/conversations" element={<SMSPhoneConversations />} />
              <Route path="sms-settings" element={<SMSSettings />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="page-projects" element={<AdminPageProjects />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="city-groups" element={<CityGroups />} />
              <Route path="featured-content" element={<FeaturedContent />} />
              <Route path="price-book" element={<PriceBook />} />
              <Route path="estimates" element={<Estimates />} />
              <Route path="estimates/builder/:id?" element={<EstimateBuilder />} />
              <Route path="proposal-gallery" element={<ProposalGallery />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/new" element={<NewCustomer />} />
          <Route path="customers/:id/edit" element={<EditCustomer />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="shingle-colors" element={<ShingleColors />} />
              <Route path="installation-phases" element={<InstallationPhases />} />
              <Route path="phase-products" element={<PhaseProducts />} />
              <Route path="product-categories" element={<ProductCategories />} />
              <Route path="tier-configuration" element={<TierConfiguration />} />
              <Route path="change-requests" element={<ChangeRequests />} />
              
              <Route path="settings" element={<AdminSettings />} />
            </Route>
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <StickyPhoneBar />
        </PhoneBarProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
